import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  ApiError,
  Button,
  apiFetch,
  buttonClasses,
  type PortalCaregiverAcceptRequest,
  type PortalCaregiverAcceptResponse,
} from "@dearbill/shared";
import { PortalShell } from "./PortalShell";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const INVITED_HEADING = "You've been invited to help manage a DearBill account";
const ACCESS_COPY =
  "Caregiver access grants full read and write access to the member's dashboard, including medication entries and support program management. It is not view-only.";
const INVALID_COPY = "This invitation is no longer valid. Ask the member to send a new one.";
const GENERIC_ERROR = "Something went wrong. Please try again.";
const SIGN_IN_HINT =
  "Sign in with your own phone number and the member's details: their Medicare number, or their name, date of birth and ZIP code.";

/** The `#token=<jwt>` fragment the invitation email's link lands with, or null. */
function readToken(): string | null {
  return new URLSearchParams(window.location.hash.slice(1)).get("token");
}

type Outcome =
  | { kind: "idle" }
  | { kind: "invalid" }
  | { kind: "error" }
  | { kind: "done"; patientFirstName: string };

/**
 * The public caregiver-invitation page at `/caregiver/accept`, outside the
 * login gate and in the signed-out shell. It reads the token from the URL
 * fragment once at mount and waits for an explicit "Accept invitation" click
 * before `POST /api/portal/caregiver/accept`, so a mail scanner that follows
 * the link cannot confirm the caregiver. Success names the member and links
 * to the login with the Caregiver role preselected; there is no automatic
 * login. A 410, like a missing token, means the invitation is no longer
 * valid; any other failure keeps the button for a retry.
 */
export function CaregiverAccept(): ReactNode {
  const [token] = useState(readToken);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(() =>
    token === null ? { kind: "invalid" } : { kind: "idle" },
  );

  async function accept(): Promise<void> {
    if (token === null) return;
    setBusy(true);
    try {
      const res = await apiFetch<PortalCaregiverAcceptResponse>(
        "/api/portal/caregiver/accept",
        { method: "POST", body: { token } satisfies PortalCaregiverAcceptRequest },
        { baseUrl: API_BASE },
      );
      setOutcome({ kind: "done", patientFirstName: res.patientFirstName });
    } catch (err) {
      setOutcome(
        err instanceof ApiError && err.status === 410 ? { kind: "invalid" } : { kind: "error" },
      );
    } finally {
      setBusy(false);
    }
  }

  let content: ReactNode;
  if (outcome.kind === "done") {
    const name = outcome.patientFirstName.trim();
    content = (
      <>
        <h1 className="text-h3 text-midnight font-serif">
          {name === ""
            ? "You're all set. You've been added as a caregiver."
            : `You're all set. ${name} added you as their caregiver.`}
        </h1>
        <p className="text-body-sm text-slate-teal mt-2">{SIGN_IN_HINT}</p>
        <Link to="/?role=caregiver" className={buttonClasses("primary", "md", "mt-8")}>
          Sign in
        </Link>
      </>
    );
  } else {
    content = (
      <>
        <h1 className="text-h3 text-midnight font-serif">{INVITED_HEADING}</h1>
        <p className="text-body-sm text-slate-teal mt-2">{ACCESS_COPY}</p>
        {outcome.kind === "invalid" && (
          <Alert tone="warning" title={INVALID_COPY} className="mt-6" />
        )}
        {outcome.kind === "error" && <Alert tone="error" title={GENERIC_ERROR} className="mt-6" />}
        {outcome.kind !== "invalid" && (
          <Button className="mt-8" disabled={busy} onClick={() => void accept()}>
            {busy ? "Accepting…" : "Accept invitation"}
          </Button>
        )}
      </>
    );
  }

  return (
    <PortalShell session={null}>
      <div>{content}</div>
    </PortalShell>
  );
}
